import { isLibraryTrack } from './library-membership.ts';
import type { MetadataBackup } from '../metadata-backup';
import type { Track } from './bridge';

type Library = { tracks: Track[]; favorites: string[]; playlists: MetadataBackup['playlists']; history: string[]; historyPlayedAt: Record<string, number>; lyrics: MetadataBackup['lyrics'] };

/** Search results are a catalogue cache, never implicit library membership. */
export function libraryCloudInput<T extends Library>(saved: T) {
  const referenced = new Set([...saved.favorites, ...saved.history, ...saved.playlists.flatMap(playlist => playlist.trackIds)]);
  const tracks = saved.tracks.filter(track => isLibraryTrack(track) || referenced.has(track.id));
  const ids = new Set(tracks.map(track => track.id));
  return { tracks, favorites: saved.favorites.filter(id => ids.has(id)), playlists: saved.playlists.map(playlist => ({ ...playlist, trackIds: playlist.trackIds.filter(id => ids.has(id)) })), history: saved.history.filter(id => ids.has(id)), historyPlayedAt: Object.fromEntries(Object.entries(saved.historyPlayedAt).filter(([id]) => ids.has(id))), lyrics: Object.fromEntries(Object.entries(saved.lyrics).filter(([id]) => ids.has(id))) };
}

/** Explicit merge adds remote relationships; existing local edits take priority. */
export function mergeLibraryCloudIndex<T extends Library>(local: T, remote: MetadataBackup) {
  const incoming = new Map(remote.tracks.map(track => [track.id, track]));
  const tracks: Track[] = local.tracks.map(track => {
    const other = incoming.get(track.id); incoming.delete(track.id);
    if (!other) return track;
    const manual = track.metadata?.localIndexLyricsOverride === true;
    return { ...other, ...track, metadata: { ...other.metadata, ...track.metadata, ...(track.sourceType === 'online' ? { libraryOwned: track.metadata?.libraryOwned === true || other.metadata?.libraryOwned === true } : {}) },
      ...(!manual && !track.lyrics?.length && other.lyrics?.length ? { lyrics: other.lyrics } : {}),
      ...(!manual && !track.amllLyrics?.length && other.amllLyrics?.length ? { amllLyrics: other.amllLyrics } : {}),
    };
  });
  tracks.push(...incoming.values());
  const playlists = local.playlists.map(playlist => {
    const other = remote.playlists.find(item => item.id === playlist.id);
    return other ? { ...other, ...playlist, trackIds: [...new Set([...playlist.trackIds, ...other.trackIds])] } : playlist;
  });
  const playlistIds = new Set(playlists.map(playlist => playlist.id));
  playlists.push(...remote.playlists.filter(playlist => !playlistIds.has(playlist.id)));
  const historyPlayedAt = { ...remote.historyPlayedAt, ...local.historyPlayedAt };
  for (const [id, time] of Object.entries(remote.historyPlayedAt)) historyPlayedAt[id] = Math.max(time, historyPlayedAt[id] || 0);
  const history = [...new Set([...local.history, ...remote.history])].sort((left, right) => (historyPlayedAt[right] || 0) - (historyPlayedAt[left] || 0)).slice(0, 1000);
  const lyrics = { ...remote.lyrics, ...local.lyrics };
  for (const track of local.tracks) if (track.metadata?.localIndexLyricsOverride === true) lyrics[track.id] = track.lyrics || [];
  return { tracks, favorites: [...new Set([...local.favorites, ...remote.favorites])], playlists, history, historyPlayedAt, lyrics };
}
